import "./ContentSidebar.scss";

// eslint-disable-next-line no-restricted-imports
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import { SECONDARY_OVERLAY_QUERY_PARAM, SIDEBAR_QUERY_PARAM } from "components/constants";
import {
  SIDEBAR_COLLAPSE_EVENT_NAME,
  SIDEBAR_EXPAND_EVENT_NAME,
  SIDEBAR_PANEL_ICONS,
  TAB_EVENT_NAMES,
  TabKeys,
  TabProps,
} from "helpers/sidebar";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { useIsXlOrLarger } from "mds/hooks/use-responsive";
import { DoubleChevronLeftIcon, DoubleChevronRightIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { pageSelectors, selectIsRecordingAgent, selectIsSidebarOpen } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useObjectSelector } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { filterMap } from "utils/collections";
import { useQueryParam, useQueryParamsActions } from "utils/routing";
import { CONTENT_SIDEBAR_TAB_NAME, ViewLevelType } from "utils/urls";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

type ContentSidebarProps = TabProps & {
  context?: ViewLevelType;
  pageId?: string;
  scrollTrackingId?: string;
};

/**
 * This sidebar component is used with various components to show the standard sidebar
 * nav items.  It's a simple tab component styled for icons.  To add a new tab add
 * a new icon to the SIDEBAR_PANEL_ICONS map.  Consumers of this component will
 * pick which tabs to display by passing in the tab content for the given tab.
 * Ex.
 *  <ContentSidebar
 *    ContentViewPanel={<div>Page View Content</div>}
 *    AiAssistantPanel={<div>AI Assistant Content</div>}
 *    ...
 *  />
 *  We pass in the `context` for logging purposes only. This corresponds
 *  to the context for the AI Assistant (if its enabled).
 */
export const ContentSidebar: FC<ContentSidebarProps> = ({
  pageId,
  context,
  scrollTrackingId = "",
  ...tabs
}) => {
  const { setQueryParams, deleteQueryParams } = useQueryParamsActions();
  const sidebarQueryParam = useQueryParam(SIDEBAR_QUERY_PARAM);
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const dispatch = useAppDispatch();
  const canShowBothSidebars = useIsXlOrLarger();

  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["category"]);

  const maybeCloseSecondarySidebar = useCallback(() => {
    if (!canShowBothSidebars) {
      setQueryParams({
        [SECONDARY_OVERLAY_QUERY_PARAM]: null,
      });
    }
  }, [canShowBothSidebars, setQueryParams]);

  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  useEffect(() => {
    if (isRecordingAgent) {
      dispatch(localStateActions.setIsSidebarOpen(false));
    }
  }, [isRecordingAgent, dispatch]);

  // Below xl, only one sidebar can be expanded at a time.
  useEffect(() => {
    if (!canShowBothSidebars && isSidebarOpen) {
      maybeCloseSecondarySidebar();
    }
  }, [canShowBothSidebars, isSidebarOpen, maybeCloseSecondarySidebar]);

  const setIsSidebarOpen = useCallback(
    (open: boolean) => {
      if (open) {
        maybeCloseSecondarySidebar();
      }
      dispatch(localStateActions.setIsSidebarOpen(open));
    },
    [dispatch, maybeCloseSecondarySidebar],
  );

  const tabNames = useMemo(
    () =>
      filterMap(
        Object.keys(tabs),
        (tab) => Boolean(tab && tabs[tab]),
        (tab) => tab.replace(/Panel$/, "") as CONTENT_SIDEBAR_TAB_NAME,
      ).sort((a, b) => TabKeys.indexOf(a) - TabKeys.indexOf(b)),
    [tabs],
  );

  // Note that this may be overridden by the SIDEBAR_QUERY_PARAM
  const defaultSidebarTab: CONTENT_SIDEBAR_TAB_NAME = useMemo(() => {
    if (!page?.category) {
      return tabNames[0];
    }
    if (page.category === "activity_page" && tabs.TopicsPanel) {
      return CONTENT_SIDEBAR_TAB_NAME.Content;
    }
    if (page.category === "assignment" && tabs.AssignmentPanel) {
      return CONTENT_SIDEBAR_TAB_NAME.Assignment;
    }
    if (page.category === "course_resource" && tabs.CourseResourcePanel) {
      return CONTENT_SIDEBAR_TAB_NAME.CourseResource;
    }
    if (page.category === "instructor_workspace" && tabs.InstructorWorkspacePanel) {
      return CONTENT_SIDEBAR_TAB_NAME.InstructorWorkspace;
    }
    if (tabs.PlannerPanel) {
      return CONTENT_SIDEBAR_TAB_NAME.Planner;
    }
    return tabNames[0];
  }, [page?.category, tabNames, tabs]);

  const [userSelectedTabKey, setUserSelectedTabKey] = useState<CONTENT_SIDEBAR_TAB_NAME | null>(
    null,
  );
  const selectedTabKey =
    userSelectedTabKey && tabNames.includes(userSelectedTabKey)
      ? userSelectedTabKey
      : tabNames.includes(defaultSidebarTab)
        ? defaultSidebarTab
        : tabNames[0];
  const selectedTabIndex = tabNames.indexOf(selectedTabKey);

  useEffect(() => {
    return () => {
      if (scrollTrackingId) {
        dispatch(
          localStateActions.setScrollPosition({
            id: scrollTrackingId,
            position: 0,
          }),
        );
      }
    };
  }, [dispatch, scrollTrackingId]);

  useEffect(() => {
    // We use SIDEBAR_QUERY_PARAM solely to determine which sidebar tab
    // should be selected when navigating to a page view.
    if (!!sidebarQueryParam && isSidebarOpen) {
      const tabKey = sidebarQueryParam as CONTENT_SIDEBAR_TAB_NAME;

      // wait until after sidebar change possibly effects search params
      const deleteQueryParamTimeout = setTimeout(() => {
        deleteQueryParams([SIDEBAR_QUERY_PARAM]);
      }, 0);

      if (tabNames.includes(tabKey)) {
        setUserSelectedTabKey(tabKey);
      }

      return () => clearTimeout(deleteQueryParamTimeout);
    }
  }, [isSidebarOpen, tabNames, sidebarQueryParam, deleteQueryParams]);

  const onSelectTab = (index: number) => {
    const shouldOpenSidebar = !isSidebarOpen;
    const newTabKey = tabNames[index];

    const eventName = TAB_EVENT_NAMES[newTabKey];
    if (eventName) {
      trackEvent(eventName, {
        ...BUTTON_EVENT_CATEGORY,
        context,
        expanded: shouldOpenSidebar,
      });
    }
    if (shouldOpenSidebar) {
      setIsSidebarOpen(true);
    }
    if (scrollTrackingId && newTabKey !== selectedTabKey) {
      dispatch(
        localStateActions.setScrollPosition({
          id: scrollTrackingId,
          position: 0,
        }),
      );
    }
    setUserSelectedTabKey(newTabKey);
  };

  const tabItems = tabNames.map((tab) => {
    const IconComponent = SIDEBAR_PANEL_ICONS[tab];

    return (
      <Tooltip content={t(`content_sidebar.title.${tab}`)} key={tab}>
        <Tab
          aria-label={t(`content_sidebar.title.${tab}`)}
          className="tab"
          onClick={() => {
            // Clicking on a tab should open the sidebar if it's closed, and log an event.
            // The `onChange` callback for the TabGroup handles that in most cases,
            // but it is not called when clicking on the currently selected tab, so we handle that case here.
            if (tab === selectedTabKey) {
              const shouldOpenSidebar = !isSidebarOpen;
              const eventName = TAB_EVENT_NAMES[tab];
              if (eventName) {
                trackEvent(eventName, {
                  ...BUTTON_EVENT_CATEGORY,
                  context,
                  expanded: shouldOpenSidebar,
                });
              }
              if (shouldOpenSidebar) {
                setIsSidebarOpen(true);
              }
            }
          }}
        >
          <IconComponent />
        </Tab>
      </Tooltip>
    );
  });

  let tabPanels: React.ReactNode = null;
  if (isSidebarOpen) {
    tabPanels = tabNames.map((tab) => {
      const tabKey = `${tab}Panel` as const;

      return (
        <TabPanel
          className="content-sidebar-panel flex h-full w-full flex-col overflow-hidden border-r-2 border-black-tint-90 bg-black-tint-95 px-3 py-[10px]"
          key={tabKey}
        >
          {tabs[tabKey]}
        </TabPanel>
      );
    });
  }

  const openStatus = isSidebarOpen ? "open" : "closed";

  return (
    <ErrorBoundary>
      <TabGroup
        aria-label={t("glossary.sidebar")}
        as="aside"
        className="content-sidebar group flex h-full w-[--sidebar-closed-width] shrink-0 rounded-l-[inherit] bg-black-tint-97 data-[sidebar-open]:w-[--sidebar-open-width]"
        data-sidebar-open={isSidebarOpen || undefined}
        selectedIndex={selectedTabIndex}
        vertical
        onChange={onSelectTab}
      >
        <TabList
          className="content-sidebar-buttons-bar flex w-[--sidebar-closed-width] flex-col self-stretch group-data-[sidebar-open]:border-r-2 group-data-[sidebar-open]:border-black-tint-90"
          data-testid="content-sidebar-tab-list"
        >
          <Tooltip content={t(`content_sidebar.${openStatus}_action`)}>
            <button
              aria-label={t(`content_sidebar.${openStatus}_action`)}
              className="tab"
              type="button"
              onClick={() => {
                trackEvent(
                  isSidebarOpen ? SIDEBAR_COLLAPSE_EVENT_NAME : SIDEBAR_EXPAND_EVENT_NAME,
                  {
                    ...BUTTON_EVENT_CATEGORY,
                    context,
                  },
                );
                setIsSidebarOpen(!isSidebarOpen);
              }}
            >
              {isSidebarOpen ? <DoubleChevronLeftIcon /> : <DoubleChevronRightIcon />}
            </button>
          </Tooltip>
          {tabItems}
        </TabList>
        <TabPanels className="w-[calc(var(--sidebar-open-width)-var(--sidebar-closed-width))]">
          {tabPanels}
        </TabPanels>
      </TabGroup>
    </ErrorBoundary>
  );
};
